import React, { useState, useMemo } from 'react';
import { KatButton, KatModal } from '@amzn/katal-react';
import { BatcherService, FunctionMetadata } from '../../services/Batcher/BatcherService';
import './BatcherPage.scss';

interface BatcherPageProps {
    stage: string;
    realm: string;
}

type StepId = 'functions' | 'input';

const STEPS: { id: StepId; label: string }[] = [
    { id: 'functions', label: 'Select Functions' },
    { id: 'input', label: 'Input' },
];

const BatcherPage: React.FC<BatcherPageProps> = ({ stage, realm }) => {
    // Functions the user has added to the chain
    const [chain, setChain] = useState<FunctionMetadata[]>([]);

    // Which step/tab is active. Navigation only happens via the Back/Next buttons.
    const [activeStep, setActiveStep] = useState<StepId>('functions');

    // Modal + picker state
    const [modalOpen, setModalOpen] = useState(false);
    const [availableFunctions, setAvailableFunctions] = useState<FunctionMetadata[]>([]);
    // Functions are fetched once (on first open) and cached for subsequent opens.
    const [functionsLoaded, setFunctionsLoaded] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // CSV row input. The header is derived from the selected functions (see csvHeader
    // below) and is not editable.
    const [csvRows, setCsvRows] = useState('');

    const PAGE_SIZE = 10;

    const filteredFunctions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return availableFunctions;
        return availableFunctions.filter(fn => fn.functionName.toLowerCase().includes(term));
    }, [availableFunctions, search]);

    const totalPages = Math.max(1, Math.ceil(filteredFunctions.length / PAGE_SIZE));
    // Clamp the page in case filtering shrank the list below the current page.
    const currentPage = Math.min(page, totalPages - 1);
    const pageFunctions = filteredFunctions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

    // CSV header: union of the selected functions' INPUT field names, in first-seen order.
    // Derived from the chain and immutable — JsonPath-style functions expect a single JSON
    // object column, so a fixed header is always correct.
    const csvHeader = useMemo(() => {
        const names: string[] = [];
        chain.forEach(fn => (fn.inputSchema || []).forEach(field => {
            if (!names.includes(field.name)) names.push(field.name);
        }));
        return names.join(',');
    }, [chain]);

    // Step navigation state.
    const stepIndex = STEPS.findIndex(s => s.id === activeStep);
    const hasInputData = csvRows.split('\n').some(line => line.trim() !== '');
    // A step must be completed before the user can advance from it.
    const stepComplete: Record<StepId, boolean> = {
        functions: chain.length > 0,
        input: hasInputData,
    };
    const canGoBack = stepIndex > 0;
    const canGoNext = stepIndex < STEPS.length - 1 && stepComplete[activeStep];

    const handleOpenModal = async () => {
        setSearch('');
        setPage(0);

        // Use the cached list if we've already fetched successfully.
        if (functionsLoaded) {
            setModalOpen(true);
            return;
        }

        setError('');
        setLoading(true);
        try {
            const functions = await BatcherService.listFunctions(stage, realm);
            setAvailableFunctions(Array.isArray(functions) ? functions : []);
            setFunctionsLoaded(true);
            setModalOpen(true);
        } catch (e: any) {
            setError(e.message || 'Failed to load functions');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const handleAddFunction = (fn: FunctionMetadata) => {
        setChain(prev => [...prev, fn]);
        setModalOpen(false);
    };

    const handleRemoveFunction = (index: number) => {
        setChain(prev => prev.filter((_, i) => i !== index));
    };

    const handleBack = () => {
        if (canGoBack) setActiveStep(STEPS[stepIndex - 1].id);
    };

    const handleNext = () => {
        if (canGoNext) setActiveStep(STEPS[stepIndex + 1].id);
    };

    return (
        <div className="batcher-page">
            <div className="batcher-header">
                <h1 className="batcher-title">Batcher</h1>
                <p className="batcher-subtitle">Select functions and provide input rows to run them as a chain.</p>
            </div>

            {/* Display-only step header. Tabs are not clickable — use Back/Next to navigate. */}
            <div className="batcher-steps" role="tablist" aria-label="Batcher steps">
                {STEPS.map((step, index) => (
                    <div
                        key={step.id}
                        role="tab"
                        aria-selected={step.id === activeStep}
                        className={`batcher-step${step.id === activeStep ? ' batcher-step-active' : ''}`}
                    >
                        <span className="batcher-step-number">{index + 1}</span>
                        <span className="batcher-step-label">{step.label}</span>
                    </div>
                ))}
            </div>

            {activeStep === 'functions' && (
                <div className="batcher-panel">
                    <div className="batcher-panel-header">
                        <span className="batcher-panel-title">Functions</span>
                        <KatButton
                            variant="secondary"
                            onClick={handleOpenModal}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : '+ Add a function to chain'}
                        </KatButton>
                    </div>

                    {error && <div className="batcher-error">{error}</div>}

                    {chain.length === 0 ? (
                        <div className="batcher-empty">No functions added</div>
                    ) : (
                        <ol className="batcher-chain">
                            {chain.map((fn, index) => (
                                <li key={`${fn.functionIdentifier}-${index}`} className="batcher-chain-item">
                                    <div className="batcher-chain-item-content">
                                    <div className="batcher-chain-item-main">
                                        <span className="batcher-chain-item-name">{fn.functionName}</span>
                                        <KatButton
                                            variant="link"
                                            onClick={() => handleRemoveFunction(index)}
                                        >
                                            Remove
                                        </KatButton>
                                    </div>
                                    <div className="batcher-chain-item-fields">
                                        {fn.inputSchema && fn.inputSchema.length > 0 ? (
                                            fn.inputSchema.map(field => (
                                                <span
                                                    key={field.name}
                                                    className="batcher-field-chip"
                                                    title={field.description}
                                                >
                                                    {field.name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="batcher-fields-label">No CSV input fields required</span>
                                        )}
                                    </div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}

            {activeStep === 'input' && (
                <div className="batcher-panel">
                    <div className="batcher-input-body">
                        <div className="batcher-input-field">
                            <label className="batcher-input-label" htmlFor="batcher-csv-header">
                                CSV header (columns)
                            </label>
                            <p className="batcher-input-hint">
                                Determined by the selected functions&apos; required fields.
                            </p>
                            <input
                                id="batcher-csv-header"
                                type="text"
                                className="batcher-csv-header"
                                value={csvHeader}
                                readOnly
                            />
                        </div>

                        <div className="batcher-input-field">
                            <label className="batcher-input-label" htmlFor="batcher-csv-rows">
                                CSV rows
                            </label>
                            <p className="batcher-input-hint">
                                One row per line, values comma-separated in the same order as the header above.
                            </p>
                            <textarea
                                id="batcher-csv-rows"
                                className="batcher-csv-rows"
                                value={csvRows}
                                onChange={e => setCsvRows(e.target.value)}
                                placeholder={csvHeader ? `${csvHeader.split(',').map(() => 'value').join(',')}` : 'value1,value2,value3'}
                                rows={12}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="batcher-nav">
                <KatButton className="batcher-nav-back" variant="secondary" onClick={handleBack} disabled={!canGoBack}>
                    Back
                </KatButton>
                <KatButton className="batcher-nav-next" variant="primary" onClick={handleNext} disabled={!canGoNext}>
                    Next
                </KatButton>
            </div>

            {modalOpen && (
            <KatModal
                visible={modalOpen}
                title="Add functions to chain"
                onClose={() => setModalOpen(false)}
            >
                <div className="batcher-modal-body">
                    <input
                        type="text"
                        className="batcher-search"
                        placeholder="Search functions by name..."
                        value={search}
                        onChange={e => handleSearchChange(e.target.value)}
                    />

                    <div className="batcher-function-list">
                        {filteredFunctions.length === 0 ? (
                            <div className="batcher-picker-empty">
                                {availableFunctions.length === 0 ? 'No functions available' : 'No functions match your search'}
                            </div>
                        ) : (
                            pageFunctions.map(fn => (
                                <button
                                    key={fn.functionIdentifier}
                                    type="button"
                                    className="batcher-function-item"
                                    onClick={() => handleAddFunction(fn)}
                                >
                                    <span className="batcher-function-item-name">{fn.functionName}</span>
                                </button>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="batcher-pagination">
                            <KatButton
                                variant="link"
                                disabled={currentPage === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                            >
                                Previous
                            </KatButton>
                            <span className="batcher-pagination-info">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <KatButton
                                variant="link"
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            >
                                Next
                            </KatButton>
                        </div>
                    )}
                </div>

                <div slot="footer" className="batcher-modal-footer">
                    <KatButton variant="link" onClick={() => setModalOpen(false)}>Cancel</KatButton>
                </div>
            </KatModal>
            )}
        </div>
    );
};

export default BatcherPage;
